/**
 * shared/fixtures/gift-ideas.ts — the IDEA fixture for the phase-2 gift flow
 * (card NYJEJRCBfBGQ).
 *
 * HONEST BY CONSTRUCTION: apiserpent credits are 0 (scripts/fixtures/apiserpent/
 * status_free_plan_2026-10-07.json), so no live product listing can ever be
 * fetched in this flow. Every entry here is therefore an AI-SUGGESTED IDEA —
 * the composer (shared/gift-ideas.ts) only ever emits source: 'idea', which
 * the data model itself keeps honest (addGiftSearchRec refuses a url on an
 * idea), and the UI renders as "Idea, AI-suggested." — never "Live listing".
 * Prices are ESTIMATES only (the card shows them with a ~), never a listing's
 * concrete price.
 *
 * Sample content is neutral: no real names, no partner names, nothing drawn
 * from any private list — a Why: line may cite only shared interests or what
 * the giver typed, and this fixture carries neither.
 */

export interface GiftIdeaTemplate {
  id: string;
  title: string;
  /** Matches the brief's KIND OF GIFT chip. */
  kind: 'thing' | 'experience' | 'meal' | 'clothing';
  /** Matches the brief's VIBE chip. */
  vibe: 'sentimental' | 'playful' | 'practical';
  /** Estimate in AUD — rendered with a ~, never as a listing price. */
  priceAud: number;
  /** Keywords the composer matches against shared interest topics and avoid
   *  items. Lowercase. */
  tags: string[];
}

export const GIFT_IDEA_TEMPLATES: readonly GiftIdeaTemplate[] = [
  { id: 'pottery-class', title: 'Pottery class for two', kind: 'experience', vibe: 'playful', priceAud: 140, tags: ['pottery', 'ceramics', 'class', 'workshop'] },
  { id: 'pourover-set', title: 'Pour-over coffee set', kind: 'thing', vibe: 'practical', priceAud: 85, tags: ['coffee', 'kitchen', 'brewing'] },
  { id: 'market-brunch', title: 'Farmers-market tour and long brunch', kind: 'meal', vibe: 'playful', priceAud: 90, tags: ['food', 'markets', 'brunch', 'outdoors'] },
  { id: 'letter-kit', title: 'Handwritten letter kit', kind: 'thing', vibe: 'sentimental', priceAud: 35, tags: ['writing', 'letters', 'stationery'] },
  { id: 'herb-garden', title: 'Indoor herb garden kit', kind: 'thing', vibe: 'practical', priceAud: 45, tags: ['plants', 'gardening', 'cooking', 'kitchen'] },
  { id: 'boardgame-cafe', title: 'Board-game café voucher for two', kind: 'experience', vibe: 'playful', priceAud: 60, tags: ['games', 'board games', 'café', 'cafe'] },
  { id: 'star-map', title: 'Custom star map of a night you both remember', kind: 'thing', vibe: 'sentimental', priceAud: 55, tags: ['stars', 'night sky', 'personal'] },
  { id: 'picnic-hamper', title: 'Picnic hamper for a park afternoon', kind: 'meal', vibe: 'sentimental', priceAud: 75, tags: ['picnic', 'outdoors', 'food'] },
  { id: 'candle-workshop', title: 'Candle-making workshop for two', kind: 'experience', vibe: 'playful', priceAud: 110, tags: ['candles', 'workshop', 'craft'] },
  { id: 'duo-books', title: 'Matching editions of a book you both love', kind: 'thing', vibe: 'sentimental', priceAud: 50, tags: ['books', 'reading'] },
  { id: 'cooking-class', title: 'Thai cooking class for two', kind: 'experience', vibe: 'practical', priceAud: 160, tags: ['cooking', 'class', 'food', 'thai'] },
  { id: 'vinyl-night', title: 'Record-player night-in kit', kind: 'thing', vibe: 'playful', priceAud: 95, tags: ['music', 'vinyl', 'records', 'night in'] },
  { id: 'scarf', title: 'Hand-woven scarf', kind: 'clothing', vibe: 'sentimental', priceAud: 120, tags: ['clothing', 'wool', 'weaving', 'accessories'] },
  { id: 'tea-tasting', title: 'Loose-leaf tea tasting box', kind: 'thing', vibe: 'practical', priceAud: 40, tags: ['tea', 'kitchen', 'tasting'] },
  { id: 'escape-room', title: 'Escape-room session for two', kind: 'experience', vibe: 'playful', priceAud: 70, tags: ['games', 'puzzle', 'escape room'] },
];
